/**
 * `live.ts` — **kanal dorong (WebSocket)**: nama topik, bentuk pesan, dan aturannya.
 *
 * K5 sudah memutuskan **push, bukan poll**, dan perangkat yang **menginisiasi koneksi keluar** —
 * karena KDS di dapur ada di balik NAT dan tak bisa dihubungi dari luar.
 *
 * ### ⭐⭐⭐ Yang dikirim adalah DENTUMAN, bukan muatan
 * Pesan di kanal ini hanya berkata *"ada yang berubah di topik X"*. Penerimanya lalu **membaca
 * ulang** lewat HTTP ke sumber yang sama dengan yang dipakai saat memuat pertama kali.
 *
 * ⛔ Alternatifnya — mengirim muatannya — membuat data yang sama punya **dua jalur** yang bisa
 * berselisih, dan satu pesan yang hilang berarti layar basi **selamanya** tanpa satu pun galat.
 * Dengan dentuman, pesan yang hilang paling buruk menunda satu pembacaan; jaring poll lambat di
 * klien menutup sisanya.
 *
 * ⚠️ Karena itu pesan di sini **tak boleh** tumbuh membawa isi. Begitu ia membawa `order`, ia jadi
 * sumber kebenaran kedua — dan yang menyimpang diam-diam selalu yang tak dibaca mesin.
 *
 * ### ⛔ Auth di PESAN PERTAMA, bukan di URL
 * Peramban tak bisa menyetel header pada WebSocket. Menaruh token di query string membuatnya
 * mendarat di log akses server, log proxy, dan riwayat — permanen, dan di tempat yang tak pernah
 * dibersihkan. Jadi koneksi dibuka dulu, lalu klien mengirim `hello` berisi tokennya; server
 * menutup koneksi bila `hello` tak datang dalam tenggat.
 *
 * @module
 */
/**
 * Topik = **satu outlet milik satu tenant**.
 *
 * ⛔⛔ `tenantCode` WAJIB ikut, dan wajib datang dari **token yang sudah diverifikasi** — tak pernah
 * dari `hello` (R-AI-1). Dua tenant boleh punya outlet berkode sama (`SMB` di dua grup berbeda);
 * topik ber-outlet saja berarti staf grup A menyimak pergerakan dapur grup B, tanpa jejak apa pun
 * karena menyimak tak menulis.
 *
 * ⚠️ Ini juga yang membuat hub tak perlu bertanya ke DB apakah outletnya milik si tenant: kalau
 * bukan miliknya, topiknya sekadar tak pernah didentum.
 */
export function liveTopic(tenantCode, outletCode) {
    return `t:${tenantCode}/outlet:${outletCode}`;
}
/**
 * Apa yang berubah. ⚠️ **Sengaja kasar** — ia cuma memberi tahu BAGIAN MANA yang perlu dibaca
 * ulang, bukan apa isinya. Menambah nilai di sini murah; menambah muatan tidak boleh.
 */
export const LIVE_KINDS = Object.freeze([
    /** Ada baris berangkat ke dapur / berubah statusnya ⇒ KDS baca ulang. */
    'kitchen',
    /** Tamu memanggil pelayan ⇒ panel panggilan baca ulang. */
    'guest_call',
    /** Pesanan ditahan/dipanggil kembali ⇒ daftar tertahan baca ulang. */
    'parked',
    /** Meja berubah (dibuka/pindah/ditutup) ⇒ dashboard meja baca ulang. */
    'table',
    /**
     * ⭐⭐ **Data master DIKIRIM dari portal** ("Send Data") ⇒ klien menampilkan tombol SYNC.
     *
     * ⛔ Dentuman ini **tidak** membuat klien menarik katalog sendiri. Ia cuma memunculkan
     * pemberitahuan; yang menekan SYNC tetap kasir. Harga yang berganti sendiri di tengah antrean
     * merampas kendali dari orang yang sedang berdiri di depan tamu — dan itu justru kelebihan
     * alur VTEC yang kita tiru, bukan keterbatasannya.
     */
    'catalog',
    /**
     * ⭐⭐ **Ada item ditandai/dicabut HABIS** ⇒ layar kasir baca ulang daftarnya, **otomatis**.
     *
     * ⛔ Sengaja TIDAK menumpang `catalog` — dan sengaja tidak menunggu SYNC. K25 menahan katalog di
     * balik tombol karena harga yang berganti sendiri di tengah antrean merampas kendali dari orang
     * yang berdiri di depan tamu. Tanda habis tak mengubah satu rupiah pun; ia cuma mencabut pilihan
     * yang barangnya memang sudah tak ada. Menahannya di balik tombol berarti terminal sebelah tetap
     * menjual ayam bakar yang habis sampai kasirnya kebetulan menekan SYNC.
     */
    'sold_out',
]);
const KIND_SET = new Set(LIVE_KINDS);
export function isLiveKind(v) {
    return typeof v === 'string' && KIND_SET.has(v);
}
/**
 * Tenggat `hello` (ms). Koneksi yang menganggur tanpa memperkenalkan diri adalah koneksi yang
 * memakan slot tanpa pernah berguna — dan di outlet, slotnya sedikit.
 */
export const LIVE_HELLO_TIMEOUT_MS = 5_000;
/**
 * ⚠️ **Jaring poll di klien tetap ada**, dan ini sengaja disebut di kontrak supaya tak dianggap
 * sisa yang lupa dibuang: proxy yang memblokir `Upgrade` akan mematikan kanal ini **tanpa satu pun
 * galat**, dan yang berhenti bergerak adalah layar dapur. Jadi klien tetap membaca ulang tiap
 * `LIVE_FALLBACK_POLL_MS` — jarang, hanya sebagai jaring.
 */
export const LIVE_FALLBACK_POLL_MS = 30_000;
/** Parse `hello` dari data kawat. ⛔ Tanpa perbaikan diam-diam: bentuk salah ⇒ `undefined`. */
export function parseLiveHello(raw) {
    if (typeof raw !== 'object' || raw === null)
        return undefined;
    const o = raw;
    if (o['type'] !== 'hello')
        return undefined;
    if (typeof o['outletCode'] !== 'string' || o['outletCode'] === '')
        return undefined;
    if (typeof o['staffToken'] !== 'string' || o['staffToken'] === '')
        return undefined;
    return { type: 'hello', outletCode: o['outletCode'], staffToken: o['staffToken'] };
}
//# sourceMappingURL=live.js.map